import { useState } from "react"
import Icon from "@/v2-ui/icon"
import MultiSelect from "@/v2-ui/multiSelect"
import RadioGroup from "@/v2-ui/radioGroup"
import { toast } from "@/v2-ui/toast"
import Button, { ConfirmButton } from "@/v2-backoffice-client/components/button"
import FeatureLockImpact from "@/v2-backoffice-client/feature/access/FeatureLockImpact"
import { useFeatureLockImpactQuery } from "@/v2-backoffice-client/feature/access/feature.access.lockImpact.query"
import type { LicenseChangesPackage } from "@/v2-backoffice-client/license/changes/license.changes.context"
import type {
  LicenseDecision,
  LicenseDecisionSubject,
  LicenseDecisionWriter,
} from "@/v2-backoffice-client/license/changes/license.changes.decision.types"
import { joinOr } from "@/v2-backoffice-client/license/changes/license.changes.utils"

export type LicenseDecisionFormProps = {
  subject: LicenseDecisionSubject
  packages: readonly LicenseChangesPackage[]
  isSaving: boolean
  /** The panel's write for this subject; the form only asks and says what happened. */
  onDecide: LicenseDecisionWriter
}

/** Customers named under the impact notice; the summary line carries the totals. */
const IMPACT_CUSTOMER_LIMIT = 8

function sameIds(a: readonly string[], b: readonly string[]) {
  return a.length === b.length && a.every((id) => b.includes(id))
}

// Who should have a feature, decided in its panel: the two answers as
// cards, and under Package-only the packages that include it, what the
// lock does in Fusion, and who would lose the feature today (the lock's
// blast radius, read before saving, never saved blind). Nothing is written
// until Save, so the feature is never package-only with nobody to carry
// it, not even for a moment; a save that takes the feature away from
// someone is confirmed on the button itself, which names the lockout. The
// draft is the form's own: the panel keys the form on the saved row, so a
// save or a switch of row starts it afresh from what is saved.
function LicenseDecisionForm(props: LicenseDecisionFormProps) {
  const { subject, packages, isSaving, onDecide } = props
  const [choice, setChoice] = useState<LicenseDecision | null>(subject.decision)
  const [draftIds, setDraftIds] = useState<string[]>([
    ...subject.pickedPackageIds,
  ])

  const isPackages = choice === "packages"
  const isDirty =
    choice !== subject.decision ||
    (isPackages && !sameIds(draftIds, subject.pickedPackageIds))

  const pickedNames = new Set(
    packages
      .filter((featureGroup) => draftIds.includes(String(featureGroup.Id)))
      .map((featureGroup) => featureGroup.Name),
  )
  // Packages that include the feature through a rule rather than a pick:
  // the picker cannot hold them, so they are named beside it.
  const ruleCarriedBy = subject.packageNames.filter(
    (packageName) => !pickedNames.has(packageName),
  )

  // Who loses the feature if it is locked now: only a flip from free has a
  // blast radius; a locked feature's packages are being edited, not its
  // availability, and the lock-out already happened.
  const isFlippingToLocked = isPackages && !subject.isLocked
  const lockImpact = useFeatureLockImpactQuery({
    variables: {
      featureCode: subject.code,
      customerLimit: IMPACT_CUSTOMER_LIMIT,
    },
    fetchPolicy: "cache-and-network",
    skip: !isFlippingToLocked,
  })
  const impact = lockImpact.data?.getFeatureLockImpact ?? undefined
  const lockedOutCount = isFlippingToLocked ? (impact?.LosingUserCount ?? 0) : 0
  // A lock is never saved before its blast radius is known: Save waits for
  // the read, and a read that failed still asks for the second click, since
  // "0" would then be the absence of an answer, not an answer.
  const isImpactUnknown = isFlippingToLocked && impact === undefined
  const isSaveDisabled =
    choice === null ||
    !isDirty ||
    isSaving ||
    (isPackages && !draftIds.length) ||
    (isImpactUnknown && lockImpact.loading)

  async function save() {
    if (choice === "everyone") {
      const saved = await onDecide("everyone")
      if (saved) toast({ title: `${subject.name} is free for everyone.` })
      return
    }
    if (choice === "packages") {
      const chosen = packages
        .filter((featureGroup) => draftIds.includes(String(featureGroup.Id)))
        .map((featureGroup) => featureGroup.Name)
      const saved = await onDecide("packages", draftIds.map(Number))
      if (saved) {
        toast({
          title: `${subject.name} is package-only: customers get it through ${joinOr(chosen)}.`,
        })
      }
    }
  }

  async function undecide() {
    const saved = await onDecide("undecided")
    if (saved) toast({ title: `${subject.name} waits on a decision again.` })
  }

  const saveLabel =
    choice === "packages"
      ? subject.isLocked
        ? "Save packages"
        : "Make package-only"
      : subject.isLocked
        ? "Open to everyone"
        : "Keep free for everyone"
  const codePrefix = `UI_Backoffice${subject.featureCodeLabel}`

  return (
    // The choices and what follows them scroll; the actions follow them
    // directly while everything fits and stay in reach at the foot of the
    // tab when the impact list runs long.
    <div className="flex min-h-0 flex-1 flex-col text-sm">
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain">
        <RadioGroup
          value={choice ?? ""}
          disabled={isSaving}
          aria-label={`Who should have ${subject.name}`}
          className="flex flex-col gap-2"
          onValueChange={(value) => {
            setChoice(value as LicenseDecision)
          }}
        >
          <RadioGroup.ChoiceCard
            id={`${subject.code}-everyone`}
            value="everyone"
            title="Free for everyone"
            description="Every customer has it, with no license check. Saving records that this is intended."
            data-feature-code={`${codePrefix}_Decide_Free`}
          />
          <RadioGroup.ChoiceCard
            id={`${subject.code}-packages`}
            value="packages"
            title="Package-only"
            description="Only customers whose license or add-on carries one of the packages named below. Everyone else sees a lock."
            data-feature-code={`${codePrefix}_Decide_Packages`}
          />
        </RadioGroup>

        {isPackages && (
          <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 p-3 text-xs">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Included in</span>
              <MultiSelect
                size="xs"
                value={draftIds}
                options={packages.map((featureGroup) => ({
                  value: String(featureGroup.Id),
                  label: featureGroup.Name,
                }))}
                placeholder="Choose the packages that include it…"
                searchPlaceholder="Search packages…"
                disabled={isSaving}
                onChange={setDraftIds}
              />
              <span className="text-muted-foreground">
                A customer needs any one of them.
                {ruleCarriedBy.length > 0
                  ? ` ${joinOr(ruleCarriedBy)} ${ruleCarriedBy.length === 1 ? "includes" : "include"} it by a rule as well.`
                  : ""}
              </span>
            </div>

            <p className="flex gap-2 text-muted-foreground">
              <Icon src="lock" size={12} className="mt-0.5 shrink-0" />
              <span>{subject.lockSentence}</span>
            </p>

            {isFlippingToLocked && (
              <FeatureLockImpact
                impact={impact}
                loading={lockImpact.loading}
                error={lockImpact.error}
                isLockedToday={false}
              />
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
        {subject.canUndecide && subject.decision !== null && (
          // Back to "nobody has said": the capability returns to free for
          // everyone and to the Needs a decision list. Confirmed because
          // for a package-only capability that is an unlock.
          <ConfirmButton
            variant="ghost"
            size="xs"
            disabled={isSaving}
            confirmContent={
              subject.isLocked ? "Unlock it for everyone" : "Back to undecided"
            }
            confirmIconSrc={subject.isLocked ? "unlock" : "check"}
            data-feature-code={`${codePrefix}_Decide_Undo`}
            onConfirm={undecide}
          >
            Take the Decision Back
          </ConfirmButton>
        )}
        {lockedOutCount > 0 || isImpactUnknown ? (
          // A save that takes the feature away from someone is confirmed on
          // the button itself (two clicks, no dialog): the first press
          // names the lockout, the second commits it. With the count
          // unreadable, the first press says so instead.
          <ConfirmButton
            variant="primary"
            size="sm"
            isLoading={isSaving}
            disabled={isSaveDisabled}
            confirmVariant="destructive"
            confirmIconSrc="lock"
            confirmContent={
              lockedOutCount > 0
                ? `Lock out ${lockedOutCount} ${lockedOutCount === 1 ? "user" : "users"}`
                : "Lock it, who loses it could not be read"
            }
            data-feature-code={`${codePrefix}_Decide_Save`}
            onConfirm={save}
          >
            {saveLabel}
          </ConfirmButton>
        ) : (
          <Button
            variant="primary"
            size="sm"
            iconSrc={choice === "packages" ? "lock" : "unlock"}
            isLoading={isSaving}
            disabled={isSaveDisabled}
            data-feature-code={`${codePrefix}_Decide_Save`}
            onClick={() => void save()}
          >
            {saveLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

export default LicenseDecisionForm
